import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { CreditCard, Banknote, ShieldCheck, Truck, Lock, Tag, ChevronRight, Sparkles, Wallet } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Checkout = () => {
  const navigate = useNavigate();
  const { cart, cartTotal, clearCart } = useCart();
  const { user } = useAuth();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponId, setCouponId] = useState(null);
  const [couponApplied, setCouponApplied] = useState(false);
  const [formData, setFormData] = useState({
    customerName: user?.name || '',
    customerEmail: user?.email || '',
    customerPhone: '',
    houseApt: '',
    colonyStreet: '',
    city: '',
    state: '',
    pincode: '',
    gstNumber: ''
  });

  useEffect(() => {
    if (cart.length === 0) { navigate('/products'); return; }
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try { const r = await axios.get(`${API}/settings`); setSettings(r.data); } catch (e) { console.error(e); }
  };

  const fullAddress = [formData.houseApt, formData.colonyStreet, formData.city, formData.state, formData.pincode].filter(Boolean).join(', ');

  const calculateTotals = () => {
    if (!settings) return { subtotal: cartTotal, gst: 0, shipping: 0, discount: 0, couponDisc: 0, total: cartTotal };
    const subtotal = cartTotal;
    const discount = (subtotal * settings.discount) / 100;
    const couponDisc = couponDiscount;
    const afterDiscount = Math.max(0, subtotal - discount - couponDisc);
    const gst = (afterDiscount * settings.gst) / 100;
    const shipping = subtotal >= settings.freeShippingThreshold ? 0 : settings.shipping;
    const extraCharges = settings.extraCharges || 0;
    return { subtotal, gst, shipping, discount, couponDisc, extraCharges, total: Math.max(0, afterDiscount + gst + shipping + extraCharges) };
  };

  const totals = calculateTotals();
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) { toast.error('Enter a coupon code'); return; }
    try {
      const res = await axios.post(`${API}/coupons/apply`, { code: couponCode, orderTotal: cartTotal });
      setCouponDiscount(res.data.discount);
      setCouponId(res.data.couponId);
      setCouponApplied(true);
      toast.success(`Coupon applied! You save ₹${res.data.discount}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon');
      setCouponDiscount(0); setCouponId(null); setCouponApplied(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode(''); setCouponDiscount(0); setCouponId(null); setCouponApplied(false);
  };

  const sendWhatsAppSummary = (orderId) => {
    const itemsList = cart.map(i => `${i.name} x${i.quantity}`).join(', ');
    const msg = encodeURIComponent(`Thank you for shopping with ZINDAL!\n\nOrder: ${orderId}\nItems: ${itemsList}\nTotal: ₹${totals.total.toFixed(2)}\nPayment: ${paymentMethod === 'cod' ? 'COD' : 'Online'}`);
    const ph = formData.customerPhone.replace(/\D/g, '');
    window.open(`https://wa.me/${ph.startsWith('91') ? ph : '91' + ph}?text=${msg}`, '_blank');
  };

  const createOrderAndFinish = async (paymentId, razorpayOrderId, status) => {
    const orderData = {
      userId: user?.uid || null, customerName: formData.customerName, customerEmail: formData.customerEmail,
      customerPhone: formData.customerPhone, address: fullAddress,
      items: cart.map(item => ({ productId: item.id, name: item.name, price: item.price, quantity: item.quantity, image: item.image })),
      subtotal: totals.subtotal, gst: totals.gst, shipping: totals.shipping, discount: totals.discount, total: totals.total,
      paymentMethod, paymentId, razorpayOrderId, status
    };
    const orderRes = await axios.post(`${API}/orders`, orderData);
    if (couponId) { try { await axios.post(`${API}/coupons/${couponId}/use`); } catch {} }
    clearCart();
    toast.success('Order placed successfully!');
    sendWhatsAppSummary(orderRes.data.orderId);
    navigate('/orders');
  };

  const openRazorpay = async () => {
    if (!window.Razorpay) { toast.error('Payment gateway not loaded. Please refresh and try again.'); setLoading(false); return; }
    const orderResponse = await axios.post(`${API}/payment/create-order`, { amount: totals.total });
    const { id: razorpay_order_id, amount, currency } = orderResponse.data;
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID, amount, currency,
      name: 'ZINDAL', description: 'Premium Lighting Solutions', order_id: razorpay_order_id,
      handler: async (response) => {
        try {
          await axios.post(`${API}/payment/verify`, { razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature });
          await createOrderAndFinish(response.razorpay_payment_id, response.razorpay_order_id, 'confirmed');
        } catch { toast.error('Payment verification failed.'); }
      },
      prefill: { name: formData.customerName, email: formData.customerEmail, contact: formData.customerPhone },
      theme: { color: '#D4AF37' }
    };
    new window.Razorpay(options).open();
    setLoading(false);
  };

  const validateForm = () => {
    const required = ['customerName', 'customerEmail', 'customerPhone', 'houseApt', 'colonyStreet', 'city', 'state', 'pincode'];
    for (const field of required) {
      if (!formData[field]?.trim()) {
        toast.error('Please fill all required fields');
        return false;
      }
    }
    if (!/^\d{6}$/.test(formData.pincode.trim())) { toast.error('Enter a valid 6-digit pincode'); return false; }
    if (!/^\d{10}$/.test(formData.customerPhone.replace(/\D/g, '').slice(-10))) { toast.error('Enter a valid 10-digit phone number'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) return;
    if (paymentMethod === 'cod' && settings.minCodAmount > 0 && totals.total < settings.minCodAmount) { toast.error(`Minimum order amount for COD is ₹${settings.minCodAmount}`); return; }
    setLoading(true);
    try {
      if (paymentMethod === 'cod') { await createOrderAndFinish('COD', null, 'confirmed'); setLoading(false); }
      else await openRazorpay();
    } catch (error) { toast.error('Checkout failed. Please try again.'); setLoading(false); }
  };

  if (!settings) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-4 border-[#D4AF37] border-t-transparent"></div></div>;
  }

  const codBlocked = paymentMethod === 'cod' && settings.minCodAmount > 0 && totals.total < settings.minCodAmount;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] via-white to-[#F0EBE0]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 py-8 md:py-12">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-[#0A192F] rounded-full flex items-center justify-center"><Lock size={18} className="text-[#D4AF37]" /></div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#0A192F]" style={{fontFamily: 'Playfair Display, serif'}} data-testid="checkout-title">Secure Checkout</h1>
            <p className="text-xs text-gray-500">SSL encrypted payment</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-6">

            {/* Payment Method */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 overflow-hidden relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#D4AF37] via-[#F5D77A] to-[#D4AF37]"></div>
              <h2 className="text-lg font-bold mb-5 flex items-center gap-2 text-[#0A192F]">
                <Sparkles size={20} className="text-[#D4AF37]" /> Choose Payment
              </h2>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { id: 'online', icon: Wallet, label: 'Pay Online', desc: 'UPI, GPay, PhonePe, Paytm', badges: ['GPay','PhPe','UPI','Paytm'] },
                  { id: 'card', icon: CreditCard, label: 'Card / Bank', desc: 'Credit, Debit, Netbanking', badges: ['VISA','MC','RuPay','NB'] },
                  { id: 'cod', icon: Banknote, label: 'Cash on Delivery', desc: 'Pay when delivered', badges: ['COD','CASH'] },
                ].map(opt => (
                  <button key={opt.id} type="button" onClick={() => setPaymentMethod(opt.id)}
                    className={`relative group rounded-xl p-4 text-center transition-all duration-300 border-2 ${
                      paymentMethod === opt.id
                        ? 'border-[#D4AF37] bg-gradient-to-br from-[#D4AF37]/10 to-[#F5D77A]/10 shadow-lg shadow-[#D4AF37]/10 scale-[1.02]'
                        : 'border-gray-200 bg-white hover:border-[#D4AF37]/40 hover:shadow-md'
                    }`} data-testid={`payment-${opt.id}`}>
                    {paymentMethod === opt.id && <div className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#D4AF37] rounded-full flex items-center justify-center"><ShieldCheck size={12} className="text-white" /></div>}
                    <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-2 transition-all ${paymentMethod === opt.id ? 'bg-[#D4AF37] text-white' : 'bg-gray-100 text-gray-500'}`}>
                      <opt.icon size={22} />
                    </div>
                    <p className="font-bold text-xs text-[#0A192F]">{opt.label}</p>
                    <p className="text-[9px] text-gray-500 mt-0.5">{opt.desc}</p>
                    <div className="flex flex-wrap justify-center gap-0.5 mt-2">
                      {opt.badges.map(b => <span key={b} className="w-7 h-3.5 rounded bg-[#0A192F] text-white text-[5px] font-bold flex items-center justify-center">{b}</span>)}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Customer Info */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
              <h2 className="text-lg font-bold mb-5 text-[#0A192F]">Billing & Delivery</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Full Name *</Label>
                  <Input name="customerName" value={formData.customerName} onChange={handleChange} className="mt-1 h-10 rounded-lg" placeholder="Full name" data-testid="customer-name-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Email *</Label>
                  <Input name="customerEmail" type="email" value={formData.customerEmail} onChange={handleChange} className="mt-1 h-10 rounded-lg" placeholder="your@email.com" data-testid="customer-email-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone Number *</Label>
                  <Input name="customerPhone" type="tel" value={formData.customerPhone} onChange={handleChange} className="mt-1 h-10 rounded-lg" placeholder="+91 98765 43210" data-testid="customer-phone-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">GST Number (optional)</Label>
                  <Input name="gstNumber" value={formData.gstNumber} onChange={handleChange} className="mt-1 h-10 rounded-lg" placeholder="22AAAAA0000A1Z5" data-testid="gst-number-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">House / Apt No. *</Label>
                  <Input name="houseApt" value={formData.houseApt} onChange={handleChange} className="mt-1 h-10 rounded-lg" placeholder="Flat 302, Tower B" data-testid="house-apt-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Colony / Street *</Label>
                  <Input name="colonyStreet" value={formData.colonyStreet} onChange={handleChange} className="mt-1 h-10 rounded-lg" placeholder="MG Road, Sector 5" data-testid="colony-street-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">City *</Label>
                  <Input name="city" value={formData.city} onChange={handleChange} className="mt-1 h-10 rounded-lg" placeholder="Mumbai" data-testid="city-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">State *</Label>
                  <Input name="state" value={formData.state} onChange={handleChange} className="mt-1 h-10 rounded-lg" placeholder="Maharashtra" data-testid="state-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Pincode *</Label>
                  <Input name="pincode" value={formData.pincode} onChange={handleChange} className="mt-1 h-10 rounded-lg" placeholder="400001" maxLength={6} data-testid="pincode-input" />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-6 py-2">
              <div className="flex items-center gap-1.5 text-gray-400 text-xs"><Lock size={14} /> SSL Encrypted</div>
              <div className="flex items-center gap-1.5 text-gray-400 text-xs"><ShieldCheck size={14} /> Secure Payments</div>
              <div className="flex items-center gap-1.5 text-gray-400 text-xs"><Truck size={14} /> Fast Delivery</div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-20">
              <h2 className="text-lg font-bold mb-5 text-[#0A192F]">Order Summary</h2>
              <div className="space-y-3 mb-5 pb-5 border-b border-gray-100">
                {cart.map((item) => (
                  <div key={item.id} className="flex gap-3 items-center" data-testid={`checkout-item-${item.id}`}>
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-[#0A192F] truncate">{item.name}</p>
                      <p className="text-gray-400 text-xs">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-bold text-sm">₹{(item.price * item.quantity).toLocaleString()}</p>
                  </div>
                ))}
              </div>

              {/* Coupon */}
              <div className="mb-5 pb-5 border-b border-gray-100">
                {couponApplied ? (
                  <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-200" data-testid="coupon-applied">
                    <div className="flex items-center gap-2">
                      <Tag size={16} className="text-green-600" />
                      <div><p className="text-sm font-bold text-green-700">{couponCode}</p><p className="text-[10px] text-green-600">You save ₹{couponDiscount}</p></div>
                    </div>
                    <button onClick={handleRemoveCoupon} className="text-red-400 text-xs hover:text-red-600 font-medium" data-testid="remove-coupon-btn">Remove</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="Coupon code" className="pl-9 h-10 rounded-lg text-sm" data-testid="coupon-input" />
                    </div>
                    <Button type="button" onClick={handleApplyCoupon} className="bg-[#0A192F] text-white hover:bg-[#0A192F]/80 h-10 px-4 rounded-lg text-xs font-bold" data-testid="apply-coupon-btn">Apply</Button>
                  </div>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="space-y-2 mb-5 text-sm">
                <div className="flex justify-between text-gray-500"><span>Subtotal</span><span data-testid="checkout-subtotal">₹{totals.subtotal.toFixed(2)}</span></div>
                {totals.discount > 0 && <div className="flex justify-between text-green-600"><span>Discount ({settings.discount}%)</span><span>-₹{totals.discount.toFixed(2)}</span></div>}
                {totals.couponDisc > 0 && <div className="flex justify-between text-green-600"><span>Coupon</span><span data-testid="coupon-discount">-₹{totals.couponDisc.toFixed(2)}</span></div>}
                <div className="flex justify-between text-gray-500"><span>GST ({settings.gst}%)</span><span data-testid="checkout-gst">₹{totals.gst.toFixed(2)}</span></div>
                <div className="flex justify-between text-gray-500"><span>Shipping</span><span data-testid="checkout-shipping">{totals.shipping === 0 ? <span className="text-green-600 font-medium">FREE</span> : `₹${totals.shipping.toFixed(2)}`}</span></div>
                {totals.extraCharges > 0 && <div className="flex justify-between text-gray-500"><span>{settings.extraChargesLabel || 'Other Charges'}</span><span>₹{totals.extraCharges.toFixed(2)}</span></div>}
              </div>

              <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2f4a] rounded-xl p-4 mb-5">
                <div className="flex justify-between items-center">
                  <span className="text-white/70 font-medium">Total</span>
                  <span className="text-2xl font-bold text-[#D4AF37]" data-testid="checkout-total">₹{totals.total.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
              </div>

              {codBlocked && <p className="text-xs text-red-500 mb-3 font-medium text-center" data-testid="cod-minimum-warning">Minimum ₹{settings.minCodAmount} for COD</p>}

              <Button onClick={handleSubmit} disabled={loading || codBlocked}
                className={`w-full h-14 text-base font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${
                  loading ? 'bg-gray-400' : 'bg-gradient-to-r from-[#D4AF37] to-[#F5D77A] text-[#0A192F] hover:shadow-lg hover:shadow-[#D4AF37]/30'
                }`} data-testid="place-order-btn">
                {loading ? (<><div className="animate-spin rounded-full h-5 w-5 border-2 border-[#0A192F] border-t-transparent"></div> Processing...</>) : (<>{paymentMethod === 'cod' ? 'Place Order (COD)' : 'Pay Now'} <ChevronRight size={18} /></>)}
              </Button>
              <p className="text-[10px] text-gray-400 mt-3 text-center">Secure checkout powered by Razorpay</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
